// Copyright 2022 Sorama B.V.

import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, bindable, Disposable } from "aurelia-framework";
import { Router } from "aurelia-router";
import { BindingSignaler } from "aurelia-templating-resources";
import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { GenericMeasurement, TimelineEntryDTO } from "datatypes/SoramaWebApi";
import html2pdf from "html2pdf.js";
import { IWorkflowModuleLifetime } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { IWorkflowModuleConfig } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ILinkedMediaObjectRepository } from "repositories/LinkedMediaObjectRepository";
import { ProjectRepository } from "repositories/ProjectRepository";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ExportService } from "services/Export/ExportService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { TimelineService } from "services/TimeLine/TimelineService";

interface ReportItem {
    timelineEntry: TimelineEntryDTO;
    visibilityStatus: boolean;
}

@autoinject
export class ReportModule implements IWorkflowModuleLifetime {
    private analysisTypesString = [
        "timeanalysis",
        "spectrumanalysis",
        "spectrogramanalysis",
        "nearfieldanalysis",
        "farfieldanalysis",
    ];
    // used in view to show any missing types in report
    protected analysisTypesStringView: string[];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    private selectedMeasurementIdForReport: string;

    public currentMeasurement: GenericMeasurement;

    @bindable
    private reportItems: ReportItem[];

    protected busyLoadingTimeline = true;

    // Stores loaded snapshots by <snapshot.id, url>
    private loadedSnapshotURLs: Map<string, string> = new Map();

    protected subscriptions: Disposable[];
    private metadata: { key: string; value: string }[] = [];
    protected isSaved = false;

    constructor(
        private readonly projectManagementService: IProjectManagementService,
        private readonly projectRepository: ProjectRepository,
        private readonly router: Router,
        private readonly linkedMediaObjectRepository: ILinkedMediaObjectRepository,
        private signaler: BindingSignaler,
        private readonly shellviewManager: ShellViewManager,
        private readonly timelineEventRepository: TimelineEntriesRepository,
        private readonly exportService: ExportService,
        private readonly ea: EventAggregator,
        private readonly timelineService: TimelineService
    ) {}

    async activateModule(): Promise<void> {
        this.subscriptions = [
            this.ea.subscribe("moduleHighlighted", (currentProject) => this.addRelevantReportItems()),
        ];
    }

    async deactivateModule(): Promise<void> {
        if (this.subscriptions) {
            this.subscriptions.forEach((s) => s.dispose());
        }
        this.disposeReportItems();
    }

    private disposeReportItems() {
        // Initialize and clear reportItem array for each analysis type
        this.reportItems = [];
        this.loadedSnapshotURLs.forEach((snapshotURL) => URL.revokeObjectURL(snapshotURL));
        this.loadedSnapshotURLs.clear();
    }

    public async selectedMeasurementIdForReportChanged(): Promise<void> {
        const measurements = this.projectManagementService.measurements.item as GenericMeasurement[];
        this.currentMeasurement = measurements.find(
            (measurement) => measurement.id == this.selectedMeasurementIdForReport
        );

        this.analysisTypesStringView = new Array(...this.analysisTypesString);

        // get metadata.
        let i = 0;
        this.metadata = [];

        try {
            if (this.currentMeasurement.metadata !== undefined) {
                for (const key of Object.keys(this.currentMeasurement.metadata)) {
                    this.metadata.push({ key: key, value: Object.values(this.currentMeasurement.metadata)[i].to });
                    i++;
                }
            }
        } catch (e) {
            // ignore for now
        }

        await this.addRelevantReportItems();
    }

    private async addRelevantReportItems() {
        if (!this.projectManagementService.currentProject.item) {
            return;
        }

        this.disposeReportItems();

        // Get all timelineEntries for the current project
        const projectTimelineEntries = this.projectRepository.getHighlightedTimelineEntries(
            this.projectManagementService.currentProject.item.id
        );
        const entryPages: TimelineEntryDTO[] = await projectTimelineEntries.readToEnd();

        this.busyLoadingTimeline = false;

        entryPages.forEach((entry: TimelineEntryDTO, index) => {
            if (entry.properties.selectedMeasurementIds) {
                // An entry can contain multiple measurements as its per project. We filter here for the selected measurement
                const isPartOfMeasurement = (entry.properties.selectedMeasurementIds as string[]).includes(
                    this.selectedMeasurementIdForReport
                );
                if (entry.highlighted && isPartOfMeasurement && entry.snapshot) {
                    this.addReportItem(entry);
                }
            }
        });
    }

    private async addReportItem(entry: TimelineEntryDTO) {
        const reportItem = {
            timelineEntry: entry,
            visibilityStatus: true,
        };

        this.reportItems.splice(this.reportItems.length, 0, reportItem);

        const typeFound = this.analysisTypesStringView.indexOf(entry.type.toString());
        if (typeFound != -1) {
            this.analysisTypesStringView.splice(typeFound, 1); //remove type from the analysisTypesString array
        }

        // Load full resolution snapshot with a temporary URL
        if (!entry.snapshot) throw Error("Could not find snapshot for entry with id " + entry.id);
        this.linkedMediaObjectRepository.getData(entry.snapshot.id).then((snapshotData) => {
            const snapshotURL = URL.createObjectURL(snapshotData);
            this.loadedSnapshotURLs.set(entry.snapshot.id, snapshotURL);
            this.signaler.signal("loaded-snapshot");
        });
    }

    protected getSnapshotSrc(reportItem: ReportItem) {
        const loadedSnapshotUrl = this.loadedSnapshotURLs.get(reportItem.timelineEntry.snapshot.id);
        if (!loadedSnapshotUrl) return reportItem.timelineEntry.snapshot.preview;
        return loadedSnapshotUrl;
    }

    protected editSnapshot(eachItem: ReportItem) {
        this.open(eachItem.timelineEntry);
    }

    protected open(entry: TimelineEntryDTO): void {
        this.timelineService.open(entry);
    }

    protected async generatePdf(): Promise<void> {
        const element = document.getElementById("element-to-print");
        if (!element) throw new Error("Element not found");

        const opt = {
            margin: 0,
            filename: "Sorama-report.pdf",
            image: { type: "jpeg" as const, quality: 0.95 },
            html2canvas: { scale: 2 },
            pagebreak: { mode: "avoid-all", after: ".report-A4" },
            jsPDF: { unit: "in", format: "a4", orientation: "portrait" as const },
        };

        const worker = html2pdf().from(element).set(opt).toPdf();

        const blob = await worker.output("blob");

        // Open in new tab
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        return blob;
    }

    public getWorkflowModuleConfig(funcName: string): IWorkflowModuleConfig {
        switch (funcName) {
            case "time":
                return AnalysisWorkflowModuleCatalog.TimeModule;
            case "spectrogram":
                return AnalysisWorkflowModuleCatalog.SpectrogramModule;
            case "spectrum":
                return AnalysisWorkflowModuleCatalog.SpectrumModule;
            case "farfield":
                return AnalysisWorkflowModuleCatalog.FarfieldModule;
            case "nearfield":
                return AnalysisWorkflowModuleCatalog.NearfieldModule;
            default:
                return undefined;
        }
    }

    protected async saveNotes(timelineEntryId: string, notes: string) {
        this.isSaved = true;
        await this.timelineEventRepository.updateNotes(timelineEntryId, notes);
        setTimeout(() => {
            this.isSaved = false;
        }, 1000);
    }

    protected changeVisibility(eachItem: ReportItem) {
        const addAttribute = document.createAttribute("data-html2canvas-ignore");
        const selectedItem = document.getElementById(eachItem.timelineEntry.id);
        const removeAttribute = selectedItem.getAttributeNode("data-html2canvas-ignore");

        if (eachItem.visibilityStatus == true) {
            selectedItem.setAttributeNode(addAttribute);
            eachItem.visibilityStatus = false;
        } else {
            selectedItem.removeAttributeNode(removeAttribute);
            eachItem.visibilityStatus = true;
        }
    }
}
